import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api, type SkillWithUsage } from "../lib/api.ts";

/**
 * Skills section on the per-agent page.
 *
 * Shows chips for currently-assigned skills. Editing opens a multi-select of
 * every skill in the registry; Save sends one PUT (one PR). Agent processes
 * pick up new skills only on restart — banner makes this explicit.
 */
export function AgentSkillsEditor({
	agentKey,
	initialSkills,
}: {
	agentKey: string;
	/** Currently assigned skill keys, as resolved by the runtime-config endpoint. */
	initialSkills: string[];
}) {
	const [allSkills, setAllSkills] = useState<SkillWithUsage[] | null>(null);
	const [assigned, setAssigned] = useState<string[]>(initialSkills);
	const [draft, setDraft] = useState<string[]>(initialSkills);
	const [editing, setEditing] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [prUrl, setPrUrl] = useState<string | null>(null);
	const [autoMerged, setAutoMerged] = useState(false);

	useEffect(() => {
		api
			.listSkills()
			.then((r) => setAllSkills(r.skills))
			.catch((err) => setError((err as Error).message));
	}, []);

	const skillsByKey = useMemo(() => {
		const map = new Map<string, SkillWithUsage>();
		for (const s of allSkills ?? []) map.set(s.key, s);
		return map;
	}, [allSkills]);

	function toggleDraft(key: string) {
		setDraft((d) => (d.includes(key) ? d.filter((k) => k !== key) : [...d, key]));
	}

	async function handleSave() {
		setBusy(true);
		setError(null);
		setPrUrl(null);
		try {
			const result = await api.setAgentSkills(agentKey, draft);
			if ("noop" in result) {
				setAssigned(draft);
				setEditing(false);
				return;
			}
			setPrUrl(result.htmlUrl);
			setAutoMerged(Boolean(result.merged));
			// optimistic update — actual visibility happens after merge+deploy
			setAssigned(draft);
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="mt-6 border border-zinc-800 rounded-lg bg-zinc-900/30">
			<header className="px-4 py-3 border-b border-zinc-800 flex items-baseline justify-between">
				<div>
					<h2 className="text-sm font-semibold text-zinc-100">Skills</h2>
					<div className="text-[11px] text-zinc-500 mt-0.5">
						{assigned.length === 0
							? "None assigned"
							: `${assigned.length} skill${assigned.length === 1 ? "" : "s"} assigned`}
					</div>
				</div>
				{!editing && (
					<button
						type="button"
						onClick={() => {
							setDraft(assigned);
							setEditing(true);
							setError(null);
							setPrUrl(null);
						}}
						className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-600 text-zinc-300"
					>
						Edit
					</button>
				)}
			</header>

			<div className="p-4 space-y-3">
				{!editing ? (
					assigned.length === 0 ? (
						<div className="text-xs text-zinc-500 italic">
							Agent has no skills. Without any, only tasks.g1.network calls happen (built into the
							CLI). Click <strong>Edit</strong> to assign tools.
						</div>
					) : (
						<div className="flex flex-wrap gap-1.5">
							{assigned.map((k) => {
								const s = skillsByKey.get(k);
								return (
									<Link
										key={k}
										to={`/skills/${encodeURIComponent(k)}`}
										className="text-xs px-2 py-1 rounded border border-zinc-700 bg-zinc-900/60 hover:border-zinc-500 text-zinc-300 font-mono"
										title={s?.description ?? k}
									>
										{s?.displayName ?? k}
									</Link>
								);
							})}
						</div>
					)
				) : (
					<>
						{allSkills === null ? (
							<div className="text-xs text-zinc-500">Loading skills…</div>
						) : allSkills.length === 0 ? (
							<div className="text-xs text-zinc-500 italic">
								No skills in the registry yet. <Link to="/skills" className="underline">Add one</Link>.
							</div>
						) : (
							<div className="space-y-1.5 max-h-64 overflow-y-auto">
								{allSkills.map((s) => {
									const checked = draft.includes(s.key);
									return (
										<label
											key={s.key}
											className="flex items-start gap-2 text-xs px-2 py-1 rounded hover:bg-zinc-800/50 cursor-pointer"
										>
											<input
												type="checkbox"
												checked={checked}
												onChange={() => toggleDraft(s.key)}
												className="mt-0.5 accent-emerald-500"
												disabled={busy}
											/>
											<div className="min-w-0 flex-1">
												<div className="flex items-baseline gap-2">
													<span className="font-medium text-zinc-200">{s.displayName}</span>
													<span className="text-[10px] text-zinc-500 font-mono">{s.key}</span>
													<span className="text-[10px] text-zinc-500">· {s.kind}</span>
												</div>
												{s.description && (
													<div className="text-[11px] text-zinc-500 mt-0.5">{s.description}</div>
												)}
											</div>
										</label>
									);
								})}
							</div>
						)}
						{error && <div className="text-xs text-red-300">{error}</div>}
						{prUrl && (
							<div className="text-xs text-emerald-300">
								{autoMerged
									? "✓ Auto-merged — restart agent to apply (Ctrl-C + re-run the npx command)."
									: "✓ PR opened — auto-merge didn't take, finish it manually."}{" "}
								<a href={prUrl} target="_blank" rel="noreferrer" className="underline break-all">
									{prUrl}
								</a>
							</div>
						)}
						<div className="flex gap-2 pt-1">
							<button
								type="button"
								onClick={handleSave}
								disabled={busy}
								className="px-3 py-1.5 bg-accent-600 hover:bg-accent-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white text-sm font-medium rounded"
							>
								{busy ? "Opening PR…" : "Save changes (PR)"}
							</button>
							<button
								type="button"
								onClick={() => setEditing(false)}
								disabled={busy}
								className="px-3 py-1.5 border border-zinc-700 hover:border-zinc-600 text-zinc-300 text-sm rounded"
							>
								Close
							</button>
						</div>
					</>
				)}
			</div>
		</section>
	);
}
